import React, { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Snackbar,
  Alert
} from '@mui/material'
import NavbarOne from './separate/NavbarOne'
import Footer from './separate/Footer'
import {
  FaRupeeSign,
  FaWallet,
  FaBolt,
  FaShieldAlt,
  FaClock,
  FaUsers,
  FaBuilding,
  FaArrowRight,
  FaCheckCircle,
  FaHeadset,
  FaChartLine,
} from 'react-icons/fa'
import { MdPercent } from 'react-icons/md'
import Config from '../core/config'
import { toast } from '../core/lib/toastAlert'
import { makeRequest } from '../core/services/v1/request'
import Captcha from '../Component/Captcha/Captcha'
import Title from '../Component/Title/Title'
import SubTitle from '../Component/SubTitle/SubTitle'
import CustomChip from '../Component/CustomChip/CustomChip'
import PageTitleDes from '../core/support/PageTitleDes'
const features = [
  {
    icon: FaRupeeSign,
    title: 'INR to Crypto',
    description:
      'Seamless conversion from INR to major cryptocurrencies with competitive rates',
  },
  {
    icon: FaWallet,
    title: 'USDT Desk',
    description:
      'Dedicated USDT trading desk for stablecoin transactions at scale',
  },
  {
    icon: MdPercent,
    title: 'Best Prices',
    description:
      'Deep liquidity pools ensuring the most competitive market rates',
  },
  {
    icon: FaBolt,
    title: 'Instant Settlement',
    description:
      'Lightning-fast settlements with real-time transaction processing',
  },
  {
    icon: FaShieldAlt,
    title: 'Secure & Compliant',
    description:
      'Full regulatory compliance with bank-grade security protocols',
  },
  {
    icon: FaHeadset,
    title: 'Dedicated Support',
    description: '24/7 personal account manager for all your trading needs',
  },
]

const benefits = [
  'No slippage on large orders',
  'Personalized pricing for volume traders',
  'Multiple payment methods supported',
  'Same-day INR settlements',
  'Flexible trade execution',
  'Complete transaction privacy',
]

const stats = [
  { value: '₹50Cr+', label: 'Monthly Volume' },
  { value: '30+', label: 'Institutional Clients' },
  { value: '<5 min', label: 'Avg Settlement Time' },
  { value: '24/7', label: 'Trading Support' },
]

const OTCDesk = (props) => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  let captchaValue = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    tradeType: '',
    amount: '',
    message: '',
  })
  const [errors, setErrors] = useState({})

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = 'Name is required'
    if (!formData.email.trim()) newErrors.email = 'Email is required'
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = 'Invalid email address'
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required'
    if (!formData.tradeType) newErrors.tradeType = 'Please select a trade type'
    if (!formData.amount.trim()) newErrors.amount = 'Amount is required'

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    setIsSubmitting(true)
    try {
      const params2 = {
        url: `${Config.V1_API_URL}contact/send-mail?type=otc`,
        method: 'POST',
        body: { ...formData, captcha_value: captchaValue.current ? captchaValue.current.getValue() : '' }
      };
      const response = await makeRequest(params2);
      if (response.status) {
        if (captchaValue.current) {
          captchaValue.current.reset();
        }
        setSnackbarOpen(true)
        if (captchaValue.current) {
          captchaValue.current.reset();
        }
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          tradeType: '',
          amount: '',
          message: '',
        })
      }
      else {
        let type = 'error';
        let message = response?.message || "Something Went Wrong";
        toast({ type, message });
      }
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box sx={{ bgcolor: '#0E1116', minHeight: '100vh', color: 'white' }}>
      <PageTitleDes title={`OTC Crypto Trading Desk | High Volume Crypto Trading Platform`} description={`Execute large cryptocurrency trades securely with our OTC Trading Desk. Get the best prices, deep liquidity, and personalized support for high-volume transactions.`} />
      <NavbarOne setTheme={props.setTheme} />

      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          pt: { xs: 10, md: 15 },
          pb: { xs: 5, md: 10 },
          overflow: 'hidden',
        }}
      >
        {/* Background Effects */}
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: '-10%',
            width: '600px',
            height: '600px',
            borderRadius: '50%',
            background: 'rgba(29, 176, 161, 0.15)',
            filter: 'blur(120px)',
            animation: 'pulse 4s infinite',
            zIndex: 0,
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '10%',
            right: '-10%',
            width: '500px',
            height: '500px',
            borderRadius: '50%',
            background: 'rgba(29, 176, 161, 0.08)',
            filter: 'blur(100px)',
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage:
              'linear-gradient(rgba(29, 176, 161, 0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(29, 176, 161, 0.03) 1px, transparent 1px)',
            backgroundSize: '50px 50px',
            zIndex: 0,
          }}
        />

        <Container maxWidth='lg' sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
            <CustomChip>
              <FaBuilding color='#1DAFA1' size={14} />
              <span style={{ color: '#1DAFA1', fontSize: '14px', fontWeight: 500 }}>
                Institutional Grade Trading
              </span>
            </CustomChip>
            <Typography
              variant='h1'
              component='h1'
              sx={{
                fontSize: { xs: '32px', md: '60px' },
                fontWeight: 800,
                lineHeight: 1.2,
                mb: 3,
                fontFamily: 'inherit',
              }}
            >
              OTC Desk for{' '}
              <Box component='span' sx={{ color: '#1DAFA1' }}>
                Large Volume
              </Box>{' '}
              Crypto Trading
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: '12px', md: '20px' },
                color: '#9AA6B2',
                mb: 5,
                lineHeight: 1.6,
                maxWidth: '650px',
                mx: 'auto',
              }}
            >
              Execute large crypto trades with zero slippage. Best INR rates,
              instant USDT settlements, and dedicated support for institutional
              and high-net-worth clients.
            </Typography>
            <br />
            <div className='d-flex flex-column flex-sm-row gap-3 justify-content-center'>
              <button
                className='btn-earn-hero'
                onClick={() => navigate('/spot/TRX_INR')}
              >
                Start Trading <FaArrowRight fontSize={20} className='ms-2' />
              </button>
              <button
                onClick={() => navigate('/help-center')}
                className='btn-earn-hero-outline'
              >
                <FaUsers fontSize={20} className='me-2' /> Talk to Sales
              </button>
            </div>
          </Box>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box
        sx={{
          py: 5,
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
          bgcolor: 'rgba(255, 255, 255, 0.02)',
        }}
      >
        <Container maxWidth='lg'>
          <Grid container spacing={4}>
            {stats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    sx={{
                      fontSize: { xs: '24px', md: '32px' },
                      fontWeight: 800,
                      color: '#1DAFA1',
                      mb: 0.5,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: 'rgba(148, 163, 184, 0.8)',
                      fontWeight: 500,
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth='lg'>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Title variant='h2'>Why Choose Our OTC Desk?</Title>
            <SubTitle>Purpose-built for institutions, funds, and high-volume traders who
              need reliable execution at scale.</SubTitle>
          </Box>

          <Grid container spacing={3}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    borderRadius: '16px',
                    height: '100%',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      borderColor: 'rgba(29, 176, 161, 0.3)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '12px',
                        bgcolor: 'rgba(29, 176, 161, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                        color: '#1DAFA1',
                      }}
                    >
                      <feature.icon size={24} />
                    </Box>
                    <Typography
                      variant='h6'
                      sx={{
                        fontSize: '20px',
                        fontWeight: 700,
                        mb: 2,
                        color: 'white',
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '14px',
                        color: 'rgba(148, 163, 184, 0.8)',
                        lineHeight: 1.6,
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          bgcolor: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <Container maxWidth='lg'>
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <Title variant='h2'>Trade Large Volumes with{' '}
              <span style={{ color: '#1DAFA1' }}>
                Confidence
              </span></Title>
            <SubTitle>Our OTC desk is designed for institutional investors, treasury
              managers, and high-net-worth individuals looking to trade crypto
              without impacting market prices.</SubTitle>
          </Box>

          <Grid container spacing={4} justifyContent='center' sx={{ mb: 3 }}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: '12px',
                    bgcolor: 'rgba(255, 255, 255, 0.02)',
                  }}
                >
                  <FaCheckCircle color='#1DAFA1' size={20} />
                  <Typography
                    sx={{
                      fontSize: '15px',
                      color: 'rgba(226, 232, 240, 0.9)',
                      fontWeight: 500,
                    }}
                  >
                    {benefit}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Minimum Trade Size Box */}
          <Box
            sx={{
              bgcolor: 'rgba(29, 176, 161, 0.1)',
              border: '1px solid rgba(29, 176, 161, 0.2)',
              borderRadius: '16px',
              p: 3,
              maxWidth: '600px',
              mx: 'auto',
              mt: 7,
            }}
          >
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  bgcolor: 'rgba(29, 176, 161, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <FaClock color='#1DAFA1' size={24} />
              </Box>
              <Box>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 700, color: 'white', mb: 1 }}
                >
                  Minimum Trade Size
                </Typography>
                <Typography
                  sx={{ fontSize: '14px', color: '#9AA6B2' }}
                >
                  ₹10 Lakhs or equivalent in crypto. For smaller amounts, please
                  use our{' '}
                  <Box
                    component='span'
                    sx={{
                      color: '#1DAFA1',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    P2P platform
                  </Box>
                  .
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Contact Form Section */}
      <Box sx={{ py: { xs: 8, md: 10 } }} id='contact-form'>
        <Container maxWidth='md'>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Title variant='h2'>Request a Quote</Title>
            <SubTitle>Fill out the form and our OTC team will contact you within 24
              hours with a personalized quote.</SubTitle>
          </Box>

          <Card
            elevation={0}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: '24px',
              backdropFilter: 'blur(20px)',
              p: { xs: 3, sm: 5 },
              mt: 7
            }}
          >
            <Box component='form' onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Name */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    component='label'
                    sx={{
                      color: 'white',
                      mb: 1,
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    Full Name *
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder='John Doe'
                    name='name'
                    value={formData.name}
                    onChange={handleInputChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    InputProps={{
                      sx: {
                        bgcolor: 'rgba(255, 255, 255, 0.04)',
                        borderRadius: '12px',
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2) !important',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1DAFA1 !important',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    component='label'
                    sx={{
                      color: 'white',
                      mb: 1,
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    Email *
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder='john@company.com'
                    name='email'
                    type='email'
                    value={formData.email}
                    onChange={handleInputChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    InputProps={{
                      sx: {
                        bgcolor: 'rgba(255, 255, 255, 0.04)',
                        borderRadius: '12px',
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2) !important',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1DAFA1 !important',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Phone */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    component='label'
                    sx={{
                      color: 'white',
                      mb: 1,
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    Phone Number *
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder='+91 98765 43210'
                    name='phone'
                    value={formData.phone}
                    onChange={handleInputChange}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    InputProps={{
                      sx: {
                        bgcolor: 'rgba(255, 255, 255, 0.04)',
                        borderRadius: '12px',
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2) !important',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1DAFA1 !important',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Company */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    component='label'
                    sx={{
                      color: 'white',
                      mb: 1,
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    Company (Optional)
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder='Company Name'
                    name='company'
                    value={formData.company}
                    onChange={handleInputChange}
                    InputProps={{
                      sx: {
                        bgcolor: 'rgba(255, 255, 255, 0.04)',
                        borderRadius: '12px',
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2) !important',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1DAFA1 !important',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Trade Type */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    component='label'
                    sx={{
                      color: 'white',
                      mb: 1,
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    Trade Type *
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    placeholder='Select trade type'
                    name='tradeType'
                    value={formData.tradeType}
                    onChange={handleInputChange}
                    error={!!errors.tradeType}
                    helperText={errors.tradeType}
                    SelectProps={{
                      displayEmpty: true,
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            bgcolor: '#0E1116',
                            border: '1px solid rgba(255,255,255,0.1)',
                            '& .MuiMenuItem-root': {
                              color: 'white',
                              '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.05)',
                              },
                              '&.Mui-selected': {
                                bgcolor: 'rgba(29, 176, 161, 0.15)',
                                '&:hover': {
                                  bgcolor: 'rgba(29, 176, 161, 0.25)',
                                },
                              },
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255, 255, 255, 0.04)',
                        borderRadius: '12px',
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2) !important',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1DAFA1 !important',
                        },
                      },
                      '& .MuiSelect-icon': {
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    <MenuItem value='' disabled>
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                        Select trade type
                      </Typography>
                    </MenuItem>
                    <MenuItem value='buy-crypto'>
                      Buy Crypto (INR to Crypto)
                    </MenuItem>
                    <MenuItem value='sell-crypto'>
                      Sell Crypto (Crypto to INR)
                    </MenuItem>
                    <MenuItem value='usdt-buy'>Buy USDT</MenuItem>
                    <MenuItem value='usdt-sell'>Sell USDT</MenuItem>
                    <MenuItem value='crypto-swap'>
                      Crypto to Crypto Swap
                    </MenuItem>
                  </TextField>
                </Grid>

                {/* Amount */}
                <Grid item xs={12} sm={6}>
                  <Typography
                    component='label'
                    sx={{
                      color: 'white',
                      mb: 1,
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    Estimated Amount *
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder='₹50,00,000 or 50 BTC'
                    name='amount'
                    type='number'
                    value={formData.amount}
                    onChange={handleInputChange}
                    error={!!errors.amount}
                    helperText={errors.amount}
                    InputProps={{
                      sx: {
                        bgcolor: 'rgba(255, 255, 255, 0.04)',
                        borderRadius: '12px',
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2) !important',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1DAFA1 !important',
                        },
                      },
                    }}
                  />
                </Grid>

                {/* Message */}
                <Grid item xs={12}>
                  <Typography
                    component='label'
                    sx={{
                      color: 'white',
                      mb: 1,
                      display: 'block',
                      fontSize: '14px',
                      fontWeight: 500,
                    }}
                  >
                    Additional Details (Optional)
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder='Tell us about your trading requirements, preferred settlement methods, or any specific needs...'
                    name='message'
                    value={formData.message}
                    onChange={handleInputChange}
                    InputProps={{
                      sx: {
                        bgcolor: 'rgba(255, 255, 255, 0.04)',
                        borderRadius: '12px',
                        color: 'white',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2) !important',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#1DAFA1 !important',
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={12}>
                  <div style={{ marginTop: '-8px', marginBottom: '-8px' }}><Captcha captchaValue={captchaValue} /></div>
                </Grid>
                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    type='submit'
                    fullWidth
                    variant='contained'
                    disabled={isSubmitting}
                    sx={{
                      bgcolor: '#1DAFA1',
                      color: 'white',
                      py: { xs: 1, md: 1.5 },
                      fontSize: { xs: '14px', md: '16px' },
                      fontWeight: 700,
                      borderRadius: '12px',
                      textTransform: 'none',
                      boxShadow: '0 4px 14px 0 rgba(29, 176, 161, 0.39)',
                      '&:hover': {
                        bgcolor: '#189a8d',
                        boxShadow: '0 6px 20px 0 rgba(29, 176, 161, 0.23)',
                      },
                      '&:disabled': {
                        bgcolor: 'rgba(29, 176, 161, 0.5)',
                      },
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                    {!isSubmitting && (
                      <FaArrowRight style={{ marginLeft: '8px' }} />
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Card>
        </Container>
      </Box>

      {/* Trust Section */}
      <Box sx={{ pt: { xs: 2, md: 2 }, pb: 10 }}>
        <Container maxWidth='lg'>
          <Box
            sx={{ textAlign: 'center', mb: 4, maxWidth: '800px', mx: 'auto' }}
          >
            <Title variant='h2'>Trusted by Leading Institutions</Title>
            <SubTitle>From crypto funds to corporate treasuries, our OTC desk handles
              the most demanding trading requirements with precision and
              confidentiality.</SubTitle>
          </Box>

          <Grid container spacing={4} justifyContent='center'>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <FaChartLine
                  size={44}
                  color='#1DAFA1'
                  style={{ marginBottom: '16px' }}
                />
                <Typography
                  variant='h5'
                  sx={{ fontSize: { xs: '18px', md: '22px' }, fontWeight: 600, mb: 1, color: 'white' }}
                >
                  Hedge Funds
                </Typography>
                <Typography
                  sx={{ color: 'rgba(148, 163, 184, 0.8)', fontSize: { xs: '12px', md: '14px' } }}
                >
                  Seamless execution for fund managers
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <FaBuilding
                  size={44}
                  color='#1DAFA1'
                  style={{ marginBottom: '16px' }}
                />
                <Typography
                  variant='h5'
                  sx={{ fontSize: { xs: '18px', md: '22px' }, fontWeight: 600, mb: 1, color: 'white' }}
                >
                  Corporates
                </Typography>
                <Typography
                  sx={{ color: 'rgba(148, 163, 184, 0.8)', fontSize: { xs: '12px', md: '14px' } }}
                >
                  Treasury management solutions
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center', p: 3 }}>
                <FaUsers
                  size={44}
                  color='#1DAFA1'
                  style={{ marginBottom: '16px' }}
                />
                <Typography
                  variant='h5'
                  sx={{ fontSize: { xs: '18px', md: '22px' }, fontWeight: 600, mb: 1, color: 'white' }}
                >
                  HNI Traders
                </Typography>
                <Typography
                  sx={{ color: 'rgba(148, 163, 184, 0.8)', fontSize: { xs: '12px', md: '14px' } }}
                >
                  White-glove service for individuals
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Footer />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity='success'
          sx={{ width: '100%' }}
        >
          Request Submitted Successfully! Our OTC team will contact you within
          24 hours.
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default OTCDesk
